import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import {
  getHandbookTemplateSections,
  getHandbookTemplateSectionsMore,
  getHandbookTemplates,
} from "pages/HandBook/model/thunks";
import { IHandBookTemplateSection } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";
import { CreateHandbookTemplateSection } from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplate";
import CreateHandbookTemplateBtn from "pages/HandBook/widgets/CreateHandbook/CreateHandbookTemplateBtn";

import HandBooksTemplatesRow from "../../HandBooksTemplatesRow";
import { ICommonColumn } from "../templates/HandBooksTemplates";
import icon from "./../../../../assets/handbook.svg";
import { useTypedSelector } from "app/store/typedUseSelector";
import TableReusableHead, { TableReusableHeaderCell } from "shared/ui/dataDisplay/TableReusable/TableReusableHead";
import BackNavigationBar from "shared/ui/layout/BackNavigationBar/BackNavigationBar";
import ListWrapper from "shared/ui/layout/ListWrapper/ListWrapper";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { IIdAndName } from "types/idAndName";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

const HandBooksTemplateSections = () => {
  const history = useHistory();
  const { templateId, sectionId } = useParams<{ templateId: string; sectionId?: string }>();
  const isSubSections = !!sectionId;
  const [filter, setFilter] = useState<any>(() => ({
    template_id: templateId,
  }));
  const hasListFilters = Object.keys(filter).length > 1;
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({
    ...restFilter,
    type: "moreTemplateSections",
  } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  // @ts-ignore | Ключ всех шаблонов – пустой фильтр
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[generateStorageKey({})];
  const sections = useTypedSelector((state) => state.handBookReducer.templateSections)[key];
  const editings = useTypedSelector((state) => state.handBookReducer.templateEditings)[HANDBOOK_TEMPLATE_LVL.SECTIONS];

  const { currentTemplate, currentSection } = useMemo(() => {
    const currentTemplate = templateId ? templates?.results?.find((t) => +t.id === +templateId) : null;
    const currentSection = sectionId ? sections?.results?.find((s) => +s.id === +sectionId) : null;
    return { currentTemplate, currentSection };
  }, [sectionId, templateId, sections, templates]);

  const entries = useMemo(() => {
    if (isSubSections) {
      const children = currentSection?.children || [];
      return { results: children, count: children.length };
    }
    return sections;
  }, [sections, isSubSections, currentSection]);

  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];

  const displaySections = useMemo(() => {
    if (!entries?.results) return entries?.results;
    return handbooksModifyWithEditings(entries.results, editings);
  }, [entries?.results, editings]);

  useEffect(() => {
    if (!templates?.results?.length) {
      dispatch(getHandbookTemplates({ limit: 500 }));
    }
    dispatch(
      getHandbookTemplateSections({
        ...filter,
        limit: Math.max(sections?.results?.length ?? 10, 10) + 1,
      })
    );
  }, [cacheKey, filter]);

  const onPagination = () => {
    if (isSubSections) return;
    dispatch(getHandbookTemplateSectionsMore({ ...filter, offset: sections?.results?.length }));
  };

  const onBack = () => {
    // Убираем крайний численный параметр
    history.replace(history.location.pathname.replace(/\/\d+\/?$/, ""));
  };

  const columns: ICommonColumn<IHandBookTemplateSection | IIdAndName>[] = [
    {
      header: { title: isSubSections ? "Подраздел" : "Раздел", filterName: isSubSections ? undefined : "name" },
      render: (item) => item.name,
    },
  ];

  return (
    <ListWrapper
      data={displaySections}
      dataMapper={(item: IHandBookTemplateSection | IIdAndName) => (
        <HandBooksTemplatesRow
          item={item}
          key={item.id}
          colPercents={colPercents}
          formColPercents={formColPercents}
          columns={columns}
          nameFieldPlaceholder={`Введите название ${isSubSections ? "подраздела" : "раздела"}`}
          onRowClick={() =>
            isSubSections ? history.push(`./${sectionId}/${item.id}`) : history.push(`./${templateId}/${item.id}`)
          }
        />
      )}
      header={
        <React.Fragment>
          <BackNavigationBar
            isInNestedList
            onBack={onBack}
            title={currentTemplate?.name || "Шаблоны"}
            slashTitle={currentSection?.name || ""}
            isBold
          />
          <TableReusableHead isSticky isNoDefaultGridCols>
            <RowGrid percents={colPercents}>
              {columns.map((el) => (
                <TableReusableHeaderCell
                  key={el.header.title}
                  isCentered={el.isCentered}
                  filterType={el.header.filterType ? el.header.filterType : el.header.filterName ? "text" : undefined}
                  filterField={el.header.filterName}
                  //@ts-ignore
                  onChangeFilter={(name, value) => setFilter((prev) => ({ ...prev, [name]: value }))}
                  filterTitle={el.header.title}
                  isLast={el.isLast}
                  filterOptions={el.header.filterOptions}
                  isRighted={el.isRighted}
                  isDebouncedFilterApply
                >
                  {el.header.title}
                </TableReusableHeaderCell>
              ))}
            </RowGrid>
          </TableReusableHead>
        </React.Fragment>
      }
      placeholderProps={{
        img: icon,
        text: hasListFilters ? "Нет сведений" : `Добавьте ${isSubSections ? "подраздел" : "раздел"}`,
        fullHeight: false
      }}
      total={entries?.count}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      filter={filter}
      onPagination={isSubSections ? undefined : onPagination}
      postfix={
        !isLoading && (
          <>
            <CreateHandbookTemplateSection
              templateId={templateId}
              sectionId={sectionId}
              nameFieldPlaceholder={`Введите название ${isSubSections ? "подраздела" : "раздела"}`}
            />
            <CreateHandbookTemplateBtn text={`Добавить ${isSubSections ? "подраздел" : "раздел"}`} />
          </>
        )
      }
    />
  );
};

export default HandBooksTemplateSections;

const colPercents = [100];
const formColPercents = [3, 97];
